import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutGrid, Settings, User, MessageCircle } from 'lucide-react';
import { HomePage } from '../pages/HomePage';
import { SetupPage } from '../pages/SetupPage';
import { ProfilePage } from '../pages/ProfilePage';
import { SupportPage } from '../pages/SupportPage';
import { OceanBackground } from './OceanBackground';
import { useUserData, useHeartbeat, useSubscription } from '../hooks/useApi';
import { AppLang, normalizeLang } from '../i18n';
import './MainLayout.css';

type TabId = 'home' | 'setup' | 'profile' | 'support';

interface MainLayoutProps {
  user: any;
  isNewUser?: boolean;
  onShowOnboarding?: () => void;
}

const TABS: { id: TabId; icon: typeof LayoutGrid }[] = [
  { id: 'home', icon: LayoutGrid },
  { id: 'setup', icon: Settings },
  { id: 'profile', icon: User },
  { id: 'support', icon: MessageCircle },
];

// Map old bot tab params to new tab system
const TAB_MAP: Record<string, { tab: TabId; sub?: string }> = {
  home: { tab: 'home' },
  vpn: { tab: 'setup' },
  setup: { tab: 'setup' },
  wallet: { tab: 'home', sub: 'subscription' },
  subscription: { tab: 'home', sub: 'subscription' },
  profile: { tab: 'profile' },
  support: { tab: 'support' },
  referral: { tab: 'profile', sub: 'referral' },
};

const getInitialRoute = (): { tab: TabId; sub: string | null } => {
  const params = new URLSearchParams(window.location.search);
  const tabParam = params.get('tab');
  if (tabParam && TAB_MAP[tabParam]) {
    return { tab: TAB_MAP[tabParam].tab, sub: TAB_MAP[tabParam].sub || null };
  }
  return { tab: 'home', sub: null };
};

export const MainLayout = ({ user, onShowOnboarding }: MainLayoutProps) => {
  const initialRoute = getInitialRoute();
  const [activeTab, setActiveTab] = useState<TabId>(initialRoute.tab);
  const [subPage, setSubPage] = useState<string | null>(initialRoute.sub);
  const { data: userData } = useUserData();
  useSubscription();
  useHeartbeat();
  const [lang, setLang] = useState<AppLang>(normalizeLang(user?.language_code));

  useEffect(() => {
    setLang(normalizeLang(userData?.language_code || user?.language_code));
  }, [userData?.language_code, user?.language_code]);

  const handleTabChange = useCallback((tabId: TabId) => {
    setActiveTab(tabId);
    setSubPage(null);
  }, []);

  const handleNavigate = useCallback((page: string) => {
    setSubPage(page);
  }, []);

  const handleBack = useCallback(() => {
    setSubPage(null);
  }, []);

  const pageProps = { user, lang, subPage, onNavigate: handleNavigate, onBack: handleBack };

  const renderContent = () => {
    const key = subPage ? `${activeTab}-${subPage}` : activeTab;
    switch (activeTab) {
      case 'home':
        return <HomePage key={key} {...pageProps} onTabChange={handleTabChange} />;
      case 'setup':
        return <SetupPage key={key} {...pageProps} onTabChange={handleTabChange} />;
      case 'profile':
        return (
          <ProfilePage
            key={key}
            {...pageProps}
            onLanguageChange={setLang}
            onShowOnboarding={onShowOnboarding}
            onTabChange={handleTabChange}
          />
        );
      case 'support':
        return <SupportPage key={key} {...pageProps} onTabChange={handleTabChange} />;
      default:
        return null;
    }
  };

  return (
    <div className="main-layout">
      <OceanBackground />
      <div className="content-area">
        <AnimatePresence mode="wait">
          <motion.div
            key={subPage ? `${activeTab}-${subPage}` : activeTab}
            initial={{ opacity: 0, x: subPage ? 20 : 0 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: subPage ? -20 : 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="page-container"
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              className={`nav-tab ${isActive ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <div className={`nav-tab-inner ${isActive ? 'active' : ''}`}>
                <Icon size={22} strokeWidth={isActive ? 2.2 : 1.8} />
              </div>
            </button>
          );
        })}
      </nav>
    </div>
  );
};
